/**
 * Version Code Generator & Decoder (Kuwait Timezone)
 * 
 * A compact date-time encoding system that converts full datetime information
 * into a short, readable version code format using Kuwait timezone (Asia/Kuwait).
 * 
 * Format: MH.YYDD.MMSS
 * - M: Month letter (A=Jan, B=Feb, ..., L=Dec)
 * - H: Hour letter (A=0, B=1, ..., X=23)
 * - YY: Year (last 2 digits, e.g., 26 for 2026)
 * - DD: Day (01-31)
 * - MM: Minute (00-59)
 * - SS: Second (00-59)
 * 
 * Example: "BH.2610.2145" represents:
 * - B = February (month 2)
 * - H = 7 AM (hour 7)
 * - 26 = Year 2026
 * - 10 = Day 10
 * - 21 = Minute 21
 * - 45 = Second 45
 * Result: February 10, 2026 at 07:21:45 (Kuwait Time)
 */

/**
 * Generate a version code from a Date object using Kuwait timezone
 * 
 * @param date - The date to encode (defaults to current date/time)
 * @returns Version code string in format MH.YYDD.MMSS
 * 
 * @example
 * const version = getVersionCode();
 * console.log(version); // "BH.2610.2145" (Kuwait time)
 * 
 * @example
 * const customDate = new Date('2026-01-15T14:30:45Z');
 * const version = getVersionCode(customDate);
 * console.log(version); // Converted to Kuwait time
 */
export const getVersionCode = (date: Date = new Date()): string => {
    // Get date parts in Kuwait timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Kuwait',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';

    const year = parseInt(getPart('year')) % 100;
    const month = parseInt(getPart('month'));
    const day = parseInt(getPart('day'));
    const hour = parseInt(getPart('hour'));
    const minute = parseInt(getPart('minute'));
    const second = parseInt(getPart('second'));

    // Convert month to letter (A=1, B=2, ..., L=12)
    const monthLetter = String.fromCharCode(64 + month);

    // Convert hour to letter (A=0, B=1, ..., X=23)
    const hourLetter = String.fromCharCode(65 + hour);

    // Pad numbers to ensure 2 digits
    const yy = ('0' + year).slice(-2);
    const dd = ('0' + day).slice(-2);
    const mm = ('0' + minute).slice(-2);
    const ss = ('0' + second).slice(-2);

    return `${monthLetter}${hourLetter}.${yy}${dd}.${mm}${ss}`;
};

/**
 * Decode a version code back to a Date object
 * Note: Decoded date is in local timezone, but represents Kuwait time values
 * 
 * @param code - Version code string in format MH.YYDD.MMSS
 * @returns Date object or null if code is invalid
 * 
 * @example
 * const decoded = decodeVersionCode("BH.2610.2145");
 * console.log(decoded); // Date object: Feb 10, 2026, 07:21:45
 * 
 * @example
 * const invalid = decodeVersionCode("invalid");
 * console.log(invalid); // null
 */
export const decodeVersionCode = (code: string): Date | null => {
    if (!code) return null;

    // Split by dots: ["MH", "YYDD", "MMSS"]
    const parts = code.split('.');
    if (parts.length !== 3) return null;

    // Validate first part has 2 characters
    if (parts[0].length !== 2) return null;

    // Validate second and third parts have 4 characters each
    if (parts[1].length !== 4 || parts[2].length !== 4) return null;

    // Extract year and day from second part
    const year = 2000 + parseInt(parts[1].substring(0, 2));
    const day = parseInt(parts[1].substring(2, 4));

    // Extract month and hour from first part (letters to numbers)
    const month = parts[0].charCodeAt(0) - 64; // A=1, B=2, ...
    const hour = parts[0].charCodeAt(1) - 65;  // A=0, B=1, ...

    // Extract minute and second from third part
    const minute = parseInt(parts[2].substring(0, 2));
    const second = parseInt(parts[2].substring(2, 4));

    // Validate ranges
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute) || isNaN(second)) {
        return null;
    }

    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (hour < 0 || hour > 23) return null;
    if (minute < 0 || minute > 59) return null;
    if (second < 0 || second > 59) return null;

    // Create Date object (month is 0-indexed in JavaScript)
    const date = new Date(year, month - 1, day, hour, minute, second);

    // Validate the date is valid (check for Invalid Date)
    if (isNaN(date.getTime())) return null;

    // Ensure the constructed date matches the parsed components (no overflow normalization)
    if (
        date.getFullYear() !== year ||
        date.getMonth() + 1 !== month ||
        date.getDate() !== day ||
        date.getHours() !== hour ||
        date.getMinutes() !== minute ||
        date.getSeconds() !== second
    ) {
        return null;
    }

    return date;
};

/**
 * Letter to Number Mappings
 *
 * Month Letters:
 * A = January (1)    G = July (7)
 * B = February (2)   H = August (8)
 * C = March (3)      I = September (9)
 * D = April (4)      J = October (10)
 * E = May (5)        K = November (11)
 * F = June (6)       L = December (12)
 *
 * Hour Letters (24-hour format - Kuwait Time):
 * A = 00:00 (midnight)  M = 12:00 (noon)
 * B = 01:00             N = 13:00
 * C = 02:00             O = 14:00
 * D = 03:00             P = 15:00
 * E = 04:00             Q = 16:00
 * F = 05:00             R = 17:00
 * G = 06:00             S = 18:00
 * H = 07:00             T = 19:00
 * I = 08:00             U = 20:00
 * J = 09:00             V = 21:00
 * K = 10:00             W = 22:00
 * L = 11:00             X = 23:00
 */

// Usage Examples:
// const version = getVersionCode(); // Uses current Kuwait time
// console.log('Version:', version);
// 
// const decoded = decodeVersionCode(version);
// console.log('Decoded:', decoded);